/**
 * `<LocationInputModal>` — root-mounted window-event host for `<LocationInput>`.
 *
 * conversation-model-versioning Phase 6 Session 1 (2026-05-18).
 *
 * ADR 0023 (VariablesPanel affordance not inline-edit): this component is
 * mounted ONCE at `routes/builder.tsx` in the sibling-modal cluster after
 * `<BrowseVersionsModal>`. It listens for the `arcanum:open-location-input`
 * CustomEvent (dispatched by the VariablesPanel affordance in Session 2) and
 * opens a modal rendering `<LocationInput>`.
 *
 * Conversation-id-match guard mirrors the `<BrowseVersionsModal>` P5 pattern:
 * the listener ignores events whose `conversationId` differs from the
 * component's `conversationId` prop so cross-route misfires are impossible.
 *
 * Session 1 leaves `onResolved` as a typed prop that Session 4 fills with the
 * actual owner-write mutation. The modal itself just closes on resolution.
 *
 * Backdrop chrome mirrors `BrowseVersionsModal` / `RestoreVersionConfirm`:
 *   `fixed inset-0 bg-void/95 backdrop-blur-sm z-50 flex items-center justify-center p-6`
 *
 * Esc closes (mirrors VersionDiffModal / BrowseVersionsModal pattern).
 * Backdrop click closes.
 */
import { useCallback, useEffect, useState } from 'react';
import { LocationInput } from './LocationInput';
import type { LocationResolvedValue } from './LocationInput';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface LocationInputModalProps {
  /**
   * The conversation ID this modal is bound to. Events carrying a different
   * id are silently ignored (conversation-id-match guard, ADR 0023).
   */
  conversationId: string | null;
  /**
   * Called when the user selects a place. ADR 0027 (Session 4): the callback
   * receives BOTH the inputKey (namedRange from the dispatching event) AND the
   * full LocationResolvedValue so the caller can fire `useModelEdit` with the
   * complete location payload (placeId + sessionToken). Session 1 left this
   * prop absent (component treated it as optional + defaulted to no-op).
   */
  onResolved?: (inputKey: string, v: LocationResolvedValue) => void;
}

// ---------------------------------------------------------------------------
// Window-event detail type
// ---------------------------------------------------------------------------

interface OpenLocationInputDetail {
  conversationId: string;
  inputKey: string;
  initialValue?: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LocationInputModal({
  conversationId,
  onResolved,
}: LocationInputModalProps) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<OpenLocationInputDetail | null>(null);

  const onClose = useCallback(() => {
    setOpen(false);
    setDetail(null);
  }, []);

  // Window-event listener — conversation-id-match guard (mirrors BrowseVersionsModal).
  useEffect(() => {
    function handleEvent(e: Event) {
      if (!(e instanceof CustomEvent)) return;
      const d = e.detail as OpenLocationInputDetail | undefined;
      if (!d?.conversationId || !d.inputKey) return;
      // Guard: ignore events that don't match this modal's conversation.
      if (d.conversationId !== conversationId) return;
      setDetail(d);
      setOpen(true);
    }
    window.addEventListener('arcanum:open-location-input', handleEvent);
    return () =>
      window.removeEventListener('arcanum:open-location-input', handleEvent);
  }, [conversationId]);

  // Esc closes (mirrors BrowseVersionsModal / VersionDiffModal pattern).
  useEffect(() => {
    if (!open) return;
    function onKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeydown);
    return () => window.removeEventListener('keydown', onKeydown);
  }, [open, onClose]);

  if (!open || !detail) return null;

  const handleResolved = (v: LocationResolvedValue) => {
    // ADR 0027: pass inputKey alongside v so the caller (builder.tsx) can
    // build the full useModelEdit vars without a separate namedRange lookup.
    if (detail) {
      onResolved?.(detail.inputKey, v);
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-void/95 backdrop-blur-sm z-50 flex items-center justify-center p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="location-input-modal-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Set property location"
        data-testid="location-input-modal"
        className="bg-panel-bg border border-panel-border rounded-[3px] shadow-xl w-full max-w-lg p-5 flex flex-col gap-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <h2 className="font-mono text-xs text-text-bright uppercase tracking-[1px]">
            Set property location
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-xs text-text-muted hover:text-text transition-colors"
            aria-label="Close location input"
            data-testid="location-input-modal-close"
          >
            Close
          </button>
        </div>

        {/* Picker */}
        <LocationInput
          conversationId={detail.conversationId}
          inputKey={detail.inputKey}
          initialValue={detail.initialValue}
          onResolved={handleResolved}
          onCancel={onClose}
          autoFocus
        />
      </div>
    </div>
  );
}
